/* Tusq · iOS Safari autofill polyfill (CL-02)
 *
 * What this fixes:
 *  1. iOS Safari refuses to autofill inputs without semantically-correct
 *     autocomplete / inputmode / type attributes. Many themes ship inputs
 *     with name="email" but no autocomplete="email" — iOS hides the autofill
 *     suggestion bar entirely.
 *  2. WebKit triggers a yellow background and overrides letter-spacing on
 *     autofilled inputs; that gets fixed in tusq-ios-autofill.css.
 *  3. Some inputs lose focus when the on-screen keyboard appears; we
 *     scroll-into-view to keep them visible.
 *
 * Strategy: a single MutationObserver-free, idempotent sweep on DOMContentLoaded
 * + a one-shot sweep on Shopify cart/checkout/account form re-renders
 * (via a custom event we expose for other Tusq scripts to call).
 */
(function () {
  'use strict';

  // Only run on iOS Safari and webview variants (others already autofill correctly)
  var ua = (navigator.userAgent || '').toString();
  var isApple = /iPad|iPhone|iPod|Macintosh/.test(ua);
  if (!isApple) return;

  // Map of (regex on name|id|placeholder) → autocomplete token
  // Order matters — first match wins.
  var RULES = [
    { re: /\b(email|e-mail|mail)\b/i,                   ac: 'email',         type: 'email',  im: 'email' },
    { re: /\b(first[\s_-]?name|fname|firstname|given)\b/i, ac: 'given-name', type: 'text' },
    { re: /\b(last[\s_-]?name|lname|lastname|family|surname)\b/i, ac: 'family-name', type: 'text' },
    { re: /\b(full[\s_-]?name|^name$|your[\s_-]?name)\b/i, ac: 'name',       type: 'text' },
    { re: /\b(company|organi[sz]ation)\b/i,             ac: 'organization',  type: 'text' },
    { re: /\b(phone|mobile|tel)\b/i,                    ac: 'tel',           type: 'tel',    im: 'tel' },
    { re: /\b(address[\s_-]?1|street|address)\b/i,      ac: 'address-line1', type: 'text' },
    { re: /\b(address[\s_-]?2|apt|apartment|suite|unit)\b/i, ac: 'address-line2', type: 'text' },
    { re: /\b(city|town)\b/i,                           ac: 'address-level2',type: 'text' },
    { re: /\b(state|province|region)\b/i,               ac: 'address-level1',type: 'text' },
    { re: /\b(zip|postal|postcode)\b/i,                 ac: 'postal-code',   type: 'text',   im: 'numeric' },
    { re: /\b(country)\b/i,                             ac: 'country',       type: 'text' },
    { re: /\b(password|passcode)\b/i,                   ac: 'current-password' },
    { re: /\b(new[\s_-]?password)\b/i,                  ac: 'new-password' },
    { re: /\b(birth|dob)\b/i,                           ac: 'bday' }
  ];

  function isInteractiveInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    var t = (el.type || '').toLowerCase();
    return t === '' || t === 'text' || t === 'email' || t === 'tel' || t === 'password' || t === 'search' || t === 'number';
  }

  function tagFromAttrs(el) {
    var hay = [
      el.getAttribute('name'),
      el.getAttribute('id'),
      el.getAttribute('placeholder'),
      el.getAttribute('aria-label'),
      el.getAttribute('data-label')
    ].filter(Boolean).join(' ');
    if (!hay) return null;
    for (var i = 0; i < RULES.length; i++) {
      if (RULES[i].re.test(hay)) return RULES[i];
    }
    return null;
  }

  function sweep(root) {
    var scope = root || document;
    var inputs = scope.querySelectorAll('input');
    var touched = 0;
    inputs.forEach(function (el) {
      if (!isInteractiveInput(el)) return;
      if (el.dataset.tusqAutofillTouched === '1') return;

      var rule = tagFromAttrs(el);
      if (!rule) {
        el.dataset.tusqAutofillTouched = '1';
        return;
      }
      // Only set if not already explicitly authored
      if (!el.getAttribute('autocomplete') && rule.ac) {
        el.setAttribute('autocomplete', rule.ac);
        touched++;
      }
      if (rule.type && !el.hasAttribute('type')) {
        el.setAttribute('type', rule.type);
      }
      if (rule.im && !el.getAttribute('inputmode')) {
        el.setAttribute('inputmode', rule.im);
      }
      // iOS-specific niceties:
      //   - turn off auto-capitalize on emails / passwords / tel
      if (rule.ac === 'email' || rule.ac === 'tel' || /password/.test(rule.ac || '')) {
        el.setAttribute('autocapitalize', 'off');
        el.setAttribute('spellcheck', 'false');
      }
      el.dataset.tusqAutofillTouched = '1';
    });
    if (touched > 0 && window.console && window.console.debug) {
      console.debug('[tusq-ios-autofill] tagged', touched, 'input(s)');
    }
  }

  // Keep the focused field above the on-screen keyboard
  function scrollFocusedIntoView(e) {
    var t = e.target;
    if (!t || !isInteractiveInput(t)) return;
    setTimeout(function () {
      try {
        t.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (err) {
        t.scrollIntoView();
      }
    }, 260); // delay until iOS keyboard slide-up settles
  }

  function init() {
    sweep(document);
    document.addEventListener('focusin', scrollFocusedIntoView, { passive: true });
    // Expose for other Tusq scripts (e.g. after AJAX form swap)
    window.tusqAutofillRescan = function (node) { sweep(node); };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
